import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { BudgetEntry, dbOperations, Category } from '../db/database';

interface BudgetContextType {
  entries: BudgetEntry[] | undefined;
  addEntry: (entry: Omit<BudgetEntry, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateEntry: (id: number, updates: Partial<BudgetEntry>) => Promise<void>;
  deleteEntry: (id: number) => Promise<void>;
  filterByCategory: Category | 'All';
  setFilterByCategory: (category: Category | 'All') => void;
  filterByType: 'all' | 'income' | 'expense';
  setFilterByType: (type: 'all' | 'income' | 'expense') => void;
  currentMonth: Date;
  setCurrentMonth: (date: Date) => void;
  monthlySummary: {
    totalIncome: number;
    totalExpenses: number;
    balance: number;
    entryCount: number;
  };
  isLoading: boolean;
}

const BudgetContext = createContext<BudgetContextType | undefined>(undefined);

export const useBudget = () => {
  const context = useContext(BudgetContext);
  if (!context) {
    throw new Error('useBudget must be used within a BudgetProvider');
  }
  return context;
};

interface BudgetProviderProps {
  children: ReactNode;
}

export const BudgetProvider: React.FC<BudgetProviderProps> = ({ children }) => {
  const [filterByCategory, setFilterByCategory] = useState<Category | 'All'>('All');
  const [filterByType, setFilterByType] = useState<'all' | 'income' | 'expense'>('all');
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [monthlySummary, setMonthlySummary] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    balance: 0,
    entryCount: 0,
  });

  // Use Dexie's live query to get real-time updates
  const allEntries = useLiveQuery(() => dbOperations.getAllEntries());

  // Filter entries based on current filters
  const entries = React.useMemo(() => {
    if (!allEntries) return undefined;

    let filtered = [...allEntries];

    // Filter by category
    if (filterByCategory !== 'All') {
      filtered = filtered.filter(e => e.category === filterByCategory);
    }

    // Filter by type
    if (filterByType !== 'all') {
      filtered = filtered.filter(e => e.type === filterByType);
    }

    return filtered;
  }, [allEntries, filterByCategory, filterByType]);

  // Update monthly summary whenever the month changes or entries change
  useEffect(() => {
    const updateSummary = async () => {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const summary = await dbOperations.getMonthlySummary(year, month);
      setMonthlySummary(summary);
    };

    updateSummary();
  }, [currentMonth, allEntries]);

  const addEntry = async (entry: Omit<BudgetEntry, 'id' | 'createdAt' | 'updatedAt'>) => {
    await dbOperations.addEntry(entry);
  };

  const updateEntry = async (id: number, updates: Partial<BudgetEntry>) => {
    await dbOperations.updateEntry(id, updates);
  };

  const deleteEntry = async (id: number) => {
    await dbOperations.deleteEntry(id);
  };

  const value: BudgetContextType = {
    entries,
    addEntry,
    updateEntry,
    deleteEntry,
    filterByCategory,
    setFilterByCategory,
    filterByType,
    setFilterByType,
    currentMonth,
    setCurrentMonth,
    monthlySummary,
    isLoading: entries === undefined,
  };

  return <BudgetContext.Provider value={value}>{children}</BudgetContext.Provider>;
};
